import React, { useState, useEffect } from 'react';
import { Button, message, Space, Tag, Tooltip, List } from 'antd';
import {
  EditOutlined,
  EyeOutlined,
  HeartOutlined,
  MessageOutlined,
  ShareAltOutlined,
  StarOutlined,
} from '@ant-design/icons';
import PageMain from '@/components/page-main';
import { useModel } from 'umi';
import { history } from 'umi';
import { listNotes } from '@/services/xiaohongshu';

interface NoteItem {
  id: number;
  title: string;
  content?: string;
  imageUrls: string[];
  tags: string[];
  ats: string[];
  isPrivate: boolean;
  area?: string;
  userId: number;
  published: boolean;
  scheduled: boolean;
  scheduledTime?: string;
  publishedNoteUrl?: string;
  publishedNoteId?: string;
  xsecToken?: string;
  isDelete: boolean;
  createdAt: string;
  updatedAt: string;
  stats: {
    views: number;
    likes: number;
    comments: number;
    shares: number;
    collects: number;
  };
  coverImage?: string;
}

const Summary: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const currentUser = initialState?.currentUser;

  const [loading, setLoading] = useState<boolean>(false);
  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 获取笔记列表
  const fetchNotes = async (page: number = 1, pageSize: number = 10) => {
    if (!currentUser?.name) {
      message.error('用户信息不存在，请重新登录');
      return;
    }

    setLoading(true);
    try {
      const result = (await listNotes({
        name: currentUser.name,
        page,
        pageSize,
      })) as any;

      if (result.resp?.code === 0) {
        // 使用真实的接口数据，添加coverImage字段
        const notesWithStats = (result.resp.data || []).map((note: any) => ({
          ...note,
          coverImage: note.imageUrls?.[0],
        }));

        setNotes(notesWithStats);
        setPagination((prev) => ({
          ...prev,
          current: page,
          total: result.resp.data.total || 0,
        }));
      } else {
        message.error(
          `获取笔记列表失败: ${result.resp?.message || '未知错误'}`,
        );
      }
    } catch (error) {
      message.error('获取笔记列表失败，请稍后重试');
      console.error('获取笔记列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 处理编辑按钮点击
  const handleEdit = (noteId: number) => {
    history.push(`/xiaohongshu/note/${noteId}`);
  };

  // 格式化数字显示
  const formatNumber = (num: number) => {
    if (num >= 10000) {
      return `${(num / 10000).toFixed(1)}w`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  // 格式化时间显示
  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    const date = new Date(timeStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (days > 0) {
      if (days === 1) return '1天前';
      if (days < 7) return `${days}天前`;
      if (days < 30) return `${Math.floor(days / 7)}周前`;
      return `${Math.floor(days / 30)}个月前`;
    } else if (hours > 0) {
      return `${hours}小时前`;
    } else {
      return '刚刚';
    }
  };

  // 处理分页变化
  const handlePageChange = (page: number, pageSize: number) => {
    fetchNotes(page, pageSize);
  };

  // 组件挂载时获取数据
  useEffect(() => {
    fetchNotes();
  }, [currentUser]);

  return (
    <PageMain>
      <div
        style={{
          margin: '24px 16px',
          padding: 24,
          background: '#fff',
          borderRadius: '8px',
          minHeight: '400px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <h2 style={{ margin: 0 }}>我的笔记</h2>
          <Button
            type="primary"
            onClick={() => history.push('/xiaohongshu/note')}
          >
            创建新笔记
          </Button>
        </div>

        <List
          itemLayout="vertical"
          loading={loading}
          dataSource={notes}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `显示 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: handlePageChange,
          }}
          renderItem={(item: NoteItem) => (
            <List.Item
              key={item.id}
              style={{
                padding: '16px',
                border: '1px solid #f0f0f0',
                borderRadius: '8px',
                marginBottom: '12px',
                backgroundColor: '#fff',
              }}
            >
              <div style={{ display: 'flex', gap: '12px' }}>
                {/* 左侧缩略图 */}
                <div style={{ flexShrink: 0 }}>
                  <img
                    src={item.coverImage}
                    alt={item.title}
                    style={{
                      width: '120px',
                      height: '90px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      backgroundColor: '#f5f5f5',
                    }}
                    // onError={(e) => {
                    //   const target = e.target as HTMLImageElement;
                    //   target.src =
                    //     'https://via.placeholder.com/120x90?text=No+Image';
                    // }}
                  />
                </div>

                {/* 右侧内容区域 */}
                <div
                  style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  {/* 标题和状态 */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        flex: 1,
                        fontSize: '16px',
                        fontWeight: 500,
                        color: '#262626',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={item.title}
                    >
                      {item.title || '(无标题)'}
                    </h3>
                    <Space size="small" style={{ marginLeft: '12px' }}>
                      {item.published ? (
                        <Tag color="green">已发布</Tag>
                      ) : item.scheduled && item.scheduledTime ? (
                        <Tooltip
                          title={`发布时间: ${new Date(
                            item.scheduledTime,
                          ).toLocaleString('zh-CN')}`}
                        >
                          <Tag color="blue">已定时</Tag>
                        </Tooltip>
                      ) : (
                        <Tag color="orange">未发布</Tag>
                      )}
                    </Space>
                  </div>

                  {/* 时间信息 */}
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '13px', color: '#8a8a8a' }}>
                      {item.createdAt ? formatTime(item.createdAt) : ''}
                      {item.createdAt && (
                        <span style={{ margin: '0 8px' }}>·</span>
                      )}
                      <span>已创建</span>
                    </span>
                  </div>

                  {/* 统计数据 */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                      }}
                    >
                      <Space size="small">
                        <EyeOutlined style={{ color: '#8a8a8a' }} />
                        <span style={{ fontSize: '13px', color: '#8a8a8a' }}>
                          {formatNumber(item.stats?.views || 0)}
                        </span>
                      </Space>
                      <Space size="small">
                        <HeartOutlined style={{ color: '#ff4d4f' }} />
                        <span style={{ fontSize: '13px', color: '#8a8a8a' }}>
                          {formatNumber(item.stats?.likes || 0)}
                        </span>
                      </Space>
                      <Space size="small">
                        <MessageOutlined style={{ color: '#1890ff' }} />
                        <span style={{ fontSize: '13px', color: '#8a8a8a' }}>
                          {formatNumber(item.stats?.comments || 0)}
                        </span>
                      </Space>
                      <Space size="small">
                        <ShareAltOutlined style={{ color: '#722ed1' }} />
                        <span style={{ fontSize: '13px', color: '#8a8a8a' }}>
                          {formatNumber(item.stats?.shares || 0)}
                        </span>
                      </Space>
                      <Space size="small">
                        <StarOutlined style={{ color: '#faad14' }} />
                        <span style={{ fontSize: '13px', color: '#8a8a8a' }}>
                          {formatNumber(item.stats?.collects || 0)}
                        </span>
                      </Space>
                    </div>

                    {/* 操作按钮 */}
                    <Space size="small">
                      {!item.published && (
                        <Button
                          type="primary"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(item.id)}
                        >
                          编辑
                        </Button>
                      )}
                      {item.published && item.publishedNoteUrl && (
                        <Button
                          type="link"
                          size="small"
                          onClick={() =>
                            window.open(item.publishedNoteUrl, '_blank')
                          }
                        >
                          查看
                        </Button>
                      )}
                    </Space>
                  </div>
                </div>
              </div>
            </List.Item>
          )}
        />
      </div>
    </PageMain>
  );
};

export default Summary;
